import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import CodeRain from "../animations/CodeRain";
import ParticleOverlay from "../animations/ParticleOverlay";
import { FaCode, FaLaptopCode, FaMobileAlt, FaDatabase, FaServer, FaCloudUploadAlt } from "react-icons/fa";
import { SiJavascript, SiReact, SiNodedotjs, SiPython, SiAngular, SiVuedotjs, SiFlutter, SiTailwindcss, SiMongodb, SiPostgresql, SiMicrosoftazure, SiAmazonwebservices, SiLinux, SiLinuxserver, SiFirebase, SiMysql } from "react-icons/si";
import { TbApi } from "react-icons/tb";
import { Link } from "react-router-dom";

// Tech stack icons with hover effects
const TechIcon = ({ Icon, translationKey }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="flex flex-col items-center justify-center p-4"
      whileHover={{ scale: 1.1, y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Icon className="text-4xl mb-2 text-primary" />
      <span className="text-xs text-gray-300">{t(translationKey)}</span>
    </motion.div>
  );
};

// Service card with animation
const ServiceCard = ({ icon: Icon, title, description }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      className="bg-black bg-opacity-50 p-6 rounded-lg border border-gray-800 hover:border-primary transition-all duration-300"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -10, boxShadow: "0 10px 20px rgba(255, 204, 0, 0.1)" }}
    >
      <div className="flex items-center mb-4">
        <div className="bg-primary bg-opacity-20 p-3 rounded-full me-4">
          <Icon className="text-primary text-2xl" />
        </div>
        <h3 className="text-xl font-bold">{title}</h3>
      </div>
      <p className="text-gray-400 text-sm">{description}</p>
    </motion.div>
  );
};

// Testimonial card component
const TestimonialCard = ({ quote, author, company, image }) => {
  return (
    <motion.div 
      className="bg-black bg-opacity-50 p-6 rounded-lg border border-gray-800"
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-start mb-4">
        <div className="mr-4">
          <div className="w-12 h-12 rounded-full overflow-hidden">
            <img src={image} alt={author} className="w-full h-full object-cover" />
          </div>
        </div>
        <div>
          <p className="text-gray-300 italic mb-4">"{quote}"</p>
          <p className="text-primary font-bold">{author}</p>
          <p className="text-gray-400 text-sm">{company}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Case study card component
const CaseStudyCard = ({ title, description, image, link }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      className="relative overflow-hidden rounded-lg group"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative h-64 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80"></div>
        <div className="absolute bottom-0 left-0 p-6 z-10">
          <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
          <p className="text-gray-300 text-sm mb-4">{description}</p>
          <Link to={link} className="inline-flex items-center text-primary hover:text-white transition-colors">
            View Case Study
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const SoftwareDevSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [techRef, techInView] = useInView({ threshold: 0.1 });
  const location = useLocation();
  
  const handleScrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('software-contact').scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Placeholder data - replace with actual data from your i18n or constants
  const services = [
    { 
      icon: FaLaptopCode, 
      title: t("software_services.custom_software.title"), 
      description: t("software_services.custom_software.description")
    },
    { 
      icon: FaMobileAlt, 
      title: t("software_services.mobile_app.title"), 
      description: t("software_services.mobile_app.description")
    },
    { 
      icon: FaCode, 
      title: t("software_services.web_app.title"), 
      description: t("software_services.web_app.description")
    },
    { 
      icon: TbApi, 
      title: t("software_services.api_dev.title"), 
      description: t("software_services.api_dev.description")
    },
    { 
      icon: FaDatabase, 
      title: t("software_services.database.title"), 
      description: t("software_services.database.description")
    },
    { 
      icon: FaCloudUploadAlt, 
      title: t("software_services.cloud.title"), 
      description: t("software_services.cloud.description")
    }
  ];

  // Testimonials data with translation keys
  const testimonials = [
    {
      quoteKey: "testimonial_1_quote",
      authorKey: "testimonial_1_author",
      companyKey: "testimonial_1_company",
      image: "https://randomuser.me/api/portraits/women/23.jpg"
    },
    {
      quoteKey: "testimonial_2_quote",
      authorKey: "testimonial_2_author",
      companyKey: "testimonial_2_company",
      image: "https://randomuser.me/api/portraits/men/36.jpg"
    }
  ];

  return (
    <div id="software-development" className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-4 md:px-8">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-20 h-[85vh] flex items-center justify-center justify-items-center relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Code Rain Animation Background */}
          <CodeRain className="opacity-80" />
          
          {/* Particle network overlay */}
          <ParticleOverlay className="opacity-70" />
          
          {/* Dark overlay to improve text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-40 z-10"></div>
          
          {/* Content */}
          <motion.div 
            className="relative z-20"
            animate={{ 
              y: [0, -10, 0],
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 6,
              ease: "easeInOut" 
            }}>
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6"
              animate={{ 
                textShadow: [
                  '0 0 5px rgba(255,204,0,0)', 
                  '0 0 15px rgba(255,204,0,0.5)', 
                  '0 0 5px rgba(255,204,0,0)'
                ]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 3,
                ease: "easeInOut" 
              }}
            >
              <span className="text-primary animate-pulse">{t("innovative")}</span> {t("software_solutions")}
            </motion.h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t("software_description")}
            </p>
            <motion.div
              className="flex flex-wrap justify-center gap-4"
              whileHover={{ scale: 1.05 }}
            >
              <a href="#software-contact" onClick={handleScrollToContact} className="bg-primary text-black px-8 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors">
                {t("start_project")}
              </a>
              <a href="#portfolio" className="border border-primary text-primary px-8 py-3 rounded-lg font-bold hover:bg-primary hover:text-black transition-colors">
                {t("view_work")}
              </a>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          id="software-services"
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 mt-24"
        >
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-12 text-center"
            variants={itemVariants}
          >
            {t("software_services_title")} <span className="text-primary"></span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard 
                key={index} 
                icon={service.icon} 
                title={service.title} 
                description={service.description} 
              />
            ))}
          </div>
        </motion.div>

        {/* Technologies Section */}
        <motion.div
          ref={techRef}
          className="mb-20 mt-24"
          initial={{ opacity: 0 }}
          animate={techInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            {t("technologies_we_master")} <span className="text-primary"></span>
          </h2>
          <div className="bg-gray-900 bg-opacity-50 rounded-xl p-8">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              <TechIcon Icon={SiJavascript} translationKey="javascript" />
              <TechIcon Icon={SiReact} translationKey="react" />
              <TechIcon Icon={SiNodedotjs} translationKey="nodejs" />
              <TechIcon Icon={SiFlutter} translationKey="flutter" />
              <TechIcon Icon={SiTailwindcss} translationKey="tailwindcss" />
              <TechIcon Icon={SiMongodb} translationKey="mongodb" />
              <TechIcon Icon={SiPostgresql} translationKey="postgresql" />
              <TechIcon Icon={SiMicrosoftazure} translationKey="azure" />
              <TechIcon Icon={SiAmazonwebservices} translationKey="aws" />
              <TechIcon Icon={SiLinuxserver} translationKey="linux_server" />
              <TechIcon Icon={SiFirebase} translationKey="firebase" />
              <TechIcon Icon={SiMysql} translationKey="mysql" />
            </div>
          </div>
        </motion.div>

        {/* Case Studies
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            {t("case_studies")} <span className="text-primary"></span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <CaseStudyCard 
              title={t("ecommerce_platform")} 
              description={t("ecommerce_platform_desc")}
              image="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
              link="/Portfolio"
            />
            <CaseStudyCard 
              title={t("healthcare_system")} 
              description={t("healthcare_system_desc")}
              image="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
              link="/Portfolio"
            />
          </div>
        </motion.div> */}

        {/* Development Process */}
        <motion.div
          className="mb-20 mt-24 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center">
            {t("our_development_process")} <span className="text-primary"></span>
          </h2>
          
          <div className="relative max-w-7xl mx-auto">
            {/* Process timeline */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-primary"></div>
            
            <div className="space-y-16 md:space-y-24 relative">
              {/* Step 1 */}
              <motion.div 
                className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} items-center relative`}
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className={`w-full md:w-1/2 ${isRTL ? 'md:pl-12' : 'md:pr-12'} text-center ${isRTL ? 'md:text-left' : 'md:text-right'} order-2 md:order-none`}>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 mt-16 md:mt-0">{t("discovery_planning")}</h3>
                  <p className="text-gray-400 text-sm sm:text-base md:max-w-lg">{t("discovery_planning_desc")}</p>
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">
                  <div className="bg-primary text-black w-full h-full rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg shadow-primary/20">1</div>
                </div>
                <div className="w-full md:w-1/2 order-1 md:order-none"></div>
              </motion.div>
              
              {/* Step 2 */}
              <motion.div 
                className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} items-center relative`}
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="w-full md:w-1/2 order-2 md:order-none"></div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">
                  <div className="bg-primary text-black w-full h-full rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg shadow-primary/20">2</div>
                </div>
                <div className={`w-full md:w-1/2 ${isRTL ? 'md:pr-12' : 'md:pl-12'} text-center ${isRTL ? 'md:text-right' : 'md:text-left'} order-1 md:order-none`}>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 mt-16 md:mt-0">{t("design_architecture")}</h3>
                  <p className="text-gray-400 text-sm sm:text-base md:max-w-lg">{t("design_architecture_desc")}</p>
                </div>
              </motion.div>
              
              {/* Step 3 */}
              <motion.div 
                className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} items-center relative`}
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className={`w-full md:w-1/2 ${isRTL ? 'md:pl-12' : 'md:pr-12'} text-center ${isRTL ? 'md:text-left' : 'md:text-right'} order-2 md:order-none`}>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 mt-16 md:mt-0">{t("development_testing")}</h3>
                  <p className="text-gray-400 text-sm sm:text-base md:max-w-lg">{t("development_testing_desc")}</p>
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">
                  <div className="bg-primary text-black w-full h-full rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg shadow-primary/20">3</div>
                </div>
                <div className="w-full md:w-1/2 order-1 md:order-none"></div>
              </motion.div>
              
              {/* Step 4 */}
              <motion.div 
                className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} items-center relative`}
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="w-full md:w-1/2 order-2 md:order-none"></div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">
                  <div className="bg-primary text-black w-full h-full rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg shadow-primary/20">4</div>
                </div>
                <div className={`w-full md:w-1/2 ${isRTL ? 'md:pr-12' : 'md:pl-12'} text-center ${isRTL ? 'md:text-right' : 'md:text-left'} order-1 md:order-none`}>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 mt-16 md:mt-0">{t("deployment_support")}</h3>
                  <p className="text-gray-400 text-sm sm:text-base md:max-w-lg">{t("deployment_support_desc")}</p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Testimonials */}
        {/* <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            {t("testimonials_title")} <span className="text-primary"></span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index}
                quote={t(testimonial.quoteKey)}
                author={t(testimonial.authorKey)}
                company={t(testimonial.companyKey)}
                image={testimonial.image}
              />
            ))}
          </div>
        </motion.div> */}

        {/* CTA Section */}
        <motion.div
          className="text-center py-16 px-4 rounded-2xl bg-gradient-to-r from-gray-900 to-black border border-gray-800 mt-24 mb-24"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{t("ready_transform")}</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            {t("discuss_expertise")}
          </p>
          <a 
            href="#software-contact" 
            onClick={handleScrollToContact}
            className="inline-block bg-primary text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
          >
            {t("software_get_in_touch")}
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default SoftwareDevSection;
